import { getOrgIdFromHostname, getOrgSlugFromHostname, isCustomDomain } from '@/core/domain';
import { applyCfCookies, rewriteResourceUrls } from '@/core/resources';
import { getAuthToken, getLinkSessionId } from './authHeader';
import { isTauri } from '@/lib/offline/isTauri';
import { tauriFetch } from '@/lib/offline/tauriFetch';
import { decodeResponseBody } from './responseBody';
import type { TypedDocumentString } from './graphql';

export { getAuthToken, getLinkSessionId } from './authHeader';

/**
 * A GraphQL call that failed at the transport level, carrying the HTTP status
 * so callers can tell "your session is gone" (401/403) from "the request
 * didn't make it" (network error, 429, 5xx). Without that distinction every
 * blip logs the user out — see `useMe`.
 *
 * The message stays `Network response was not ok` for non-auth statuses:
 * `SharedPage` matches on it.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The first GraphQL error message of a non-2xx response that carried one
   * (Apollo answers a validation / variable error with HTTP 400 and a body),
   * so a caller can tell "the server refused this" from "it never arrived".
   */
  readonly serverMessage?: string;

  constructor(message: string, status: number, serverMessage?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (serverMessage) this.serverMessage = serverMessage;
  }

  /** The credential was rejected — the only case that should end the session. */
  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }
}

export async function execute<TResult, TVariables>(
  query: TypedDocumentString<TResult, TVariables>,
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
) {
  const url = new URL(import.meta.env.VITE_ENDPOINT);
  url.pathname = '/gql';

  const orgId = getOrgIdFromHostname();
  const slug = getOrgSlugFromHostname();

  const authorization = getAuthToken();

  // The authorizer caches on the `Authorization` header, which makes it a
  // required identity source: API Gateway answers an empty one with a 401
  // before the Lambda (and its cookie fallback) ever runs. Firing the request
  // anyway just turns "no credential yet" into a server 401 the app then reads
  // as "your session died".
  if (!authorization) throw new ApiError('UNAUTHENTICATED', 401);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/graphql-response+json',
    Authorization: authorization,
  };

  const linkSessionId = getLinkSessionId();
  if (linkSessionId) headers['X-Link-Session'] = linkSessionId;

  if (orgId) headers['x-organisation'] = orgId;
  else if (slug) headers['X-Organization-Slug'] = slug;

  const projectMatch = window.location.pathname.match(/^\/projects\/([^/]+)/);
  if (projectMatch) headers['x-project-id'] = projectMatch[1];

  // In Tauri the WKWebView can't carry the auth/CloudFront cookies cross-origin,
  // so route the API through Rust (`tauriFetch`) and its shared cookie jar.
  const doFetch = isTauri() ? tauriFetch : fetch;
  const response = await doFetch(url.toString(), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    let serverMessage: string | undefined;
    try {
      const body = JSON.parse(await response.text()) as { errors?: { message?: unknown }[] };
      const m = body?.errors?.[0]?.message;
      if (typeof m === 'string' && m.trim()) serverMessage = m.trim();
    } catch {
      // not a GraphQL body (gateway error page, empty): nothing to add
    }
    throw new ApiError('Network response was not ok', response.status, serverMessage);
  }

  const res = await decodeResponseBody(await response.text());

  // Foreign (custom-domain) origins can't receive the CloudFront signed
  // cookies via response headers (Set-Cookie is scrubbed cross-origin), so
  // Task 6's resolver ships them in extensions instead — apply them here,
  // first-party on the current origin. skygis.app origins get them via
  // headers already, so don't double-apply from this path.
  if (res?.extensions?.cfCookies && isCustomDomain()) applyCfCookies(res.extensions.cfCookies);
  // Partial-success responses ({ data, errors }) intentionally return data —
  // pre-existing contract predating the custom-domain change; react-query
  // callers rely on getting partial data instead of a hard failure.
  if (res?.data) return rewriteResourceUrls(res.data) as TResult;
  if (res?.errors) throw new Error(res.errors[0].message);
  throw new Error('Empty GraphQL response');
}
